import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import Header from '../../components/Header/Header';
import { HeyGenAvatar, type HeyGenAvatarHandle } from '../../components/HeyGenAvatar';
import { useWebSpeechSTT } from '../../components/HeyGenAvatar/hooks/useWebSpeechSTT';
import { LanguageSelector } from '../../components/LanguageSelector';
import { LiveChatOverlay } from '../../components/LiveChatOverlay';
import { MessageContent } from '../../components/MessageContent';
import { SessionEndedOverlay } from '../../components/SessionEndedOverlay';
import { VideoPlayer } from '../../components/VideoPlayer';
import { VoiceRecordingBar } from '../../components/VoiceRecordingBar';
import { AVATARS, getLocalizedAvatarInfo } from '../../config/avatarMapping';
import {
  deleteConversation,
  getChatHistory,
  getUserConversations,
  sendChatMessage,
  updateConversation,
} from '../../services/chatService';
import { getIdToken, isAuthenticated, signOut } from '../../services/cognitoService';
import { checkHeyGenAccess } from '../../services/heygenService';
import { checkoutStripeCredits, purchaseAddonDirect } from '../../services/revenuecatService';
import './Chat.css';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  tokenUsage?: {
    totalTokens: number;
    contextWindowSize: number;
    historyTruncated: boolean;
  };
}

interface ChatSession {
  id: string;
  title: string;
  date: string;
  dateGroup: string;
  avatarId: string;
  avatarName?: string;
}

// Monotonically increasing counter to guarantee unique IDs even within the same ms
let _msgIdCounter = 0;
const uniqueId = (prefix: string): string => `${prefix}-${Date.now()}-${++_msgIdCounter}`;

/**
 * Append new messages to the list, skipping any whose id already exists.
 * This prevents duplicates from double-clicks, race conditions, or history reload.
 */
const appendMessagesIfNew = (prev: Message[], ...newMessages: Message[]): Message[] => {
  const existingIds = new Set(prev.map((m) => m.id));
  const deduped = newMessages.filter((m) => !existingIds.has(m.id));
  return deduped.length > 0 ? [...prev, ...deduped] : prev;
};

// Helper to safely format time
const formatTime = (dateStr: string | undefined | null): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

// Helper to safely format date
const formatDate = (dateStr: string | undefined | null): string => {
  if (!dateStr) return 'Today';
  try {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return 'Today';
    return date.toLocaleDateString();
  } catch {
    return 'Today';
  }
};

// Helper to format date for grouping
const getDateGroup = (dateStr: string | undefined | null): string => {
  if (!dateStr) return 'today';
  try {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return 'today';
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'today';
    if (diffDays <= 7) return 'week';
    return 'older';
  } catch {
    return 'today';
  }
};

const Chat: React.FC = () => {
  const { companionId } = useParams<{ companionId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const [initialAuthCheck] = useState(() => isAuthenticated());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const heyGenAvatarRef = useRef<HeyGenAvatarHandle>(null);
  // Tracks a freshly-created conversationId so we skip reloading history for it;
  // the history reload is only needed when navigating to an existing session.
  const skipHistoryForConversationRef = useRef<string | null>(null);

  // STT Language
  const [sttLanguage, setSttLanguage] = useState('en-US');

  // Panel resize state — ratio of avatar panel width in percent
  const [splitRatio, setSplitRatio] = useState(50);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartRatioRef = useRef(50);
  const splitContainerRef = useRef<HTMLDivElement>(null);

  // State
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [, setIsLoadingHistory] = useState(true);
  const [showSidebar, setShowSidebar] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  // Inline error state for failed messages (network/timeout errors)
  const [failedMessageId, setFailedMessageId] = useState<string | null>(null);
  const failedMessageContentRef = useRef<string | null>(null);

  // Detect mobile/tablet and handle sidebar visibility
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const mobile = width <= 1024;
      setIsMobile(mobile);

      // Keep sidebar closed by default on all screen sizes
      // User can open it manually with the hamburger button
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [searchQuery, setSearchQuery] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(searchParams.get('session'));
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showCreditModal, setShowCreditModal] = useState<false | 'daily_limit' | 'no_credits'>(
    false,
  );
  const [addonPurchaseState, setAddonPurchaseState] = useState<{
    loading: string | null;
    success: boolean;
    error: string | null;
  }>({ loading: null, success: false, error: null });
  const [isLoggedIn, setIsLoggedIn] = useState(initialAuthCheck);

  // Avatar mode state - now defaults to showing video with chat side by side
  const [isLiveSession, setIsLiveSession] = useState(false);
  const [canUseAvatar, setCanUseAvatar] = useState(false);
  const [showMobileChatOverlay, setShowMobileChatOverlay] = useState(false);
  // Desktop chat state (sidebar visibility)
  const [showDesktopChat, setShowDesktopChat] = useState(true);
  // Session ended overlay state
  const [showSessionEnded, setShowSessionEnded] = useState(false);
  const [isOverlayFocused, setIsOverlayFocused] = useState(false);
  // Tracks the window between tapping LIVE and avatar fully connecting (hides chat overlay during loading)
  const [isAvatarConnecting, setIsAvatarConnecting] = useState(false);
  // Tracks whether HeyGenAvatar's chrome (controls + input) is auto-hidden
  const [avatarChromeHidden, setAvatarChromeHidden] = useState(false);

  // ── Extras menu (+ button) state ──
  const [showExtrasMenu, setShowExtrasMenu] = useState(false);
  const extrasMenuRef = useRef<HTMLDivElement>(null);
  const [showFollowUpQuestions, setShowFollowUpQuestions] = useState(() => {
    try {
      return localStorage.getItem('theychat_followup_enabled') !== 'false';
    } catch {
      return true;
    }
  });

  const toggleFollowUpQuestions = useCallback(() => {
    setShowFollowUpQuestions((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('theychat_followup_enabled', String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const toggleExtrasMenu = useCallback(() => {
    setShowExtrasMenu((prev) => !prev);
  }, []);

  // Handle resize drag interactions
  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isMobile) return;
      isDraggingRef.current = true;
      dragStartXRef.current = e.clientX;
      dragStartRatioRef.current = splitRatio;

      const onMouseMove = (moveEvent: MouseEvent) => {
        if (!isDraggingRef.current || !splitContainerRef.current) return;
        const containerWidth = splitContainerRef.current.getBoundingClientRect().width;
        const deltaX = moveEvent.clientX - dragStartXRef.current;
        const deltaPct = (deltaX / containerWidth) * 100;
        const newRatio = Math.min(80, Math.max(20, dragStartRatioRef.current + deltaPct));
        setSplitRatio(newRatio);
      };

      const onMouseUp = () => {
        isDraggingRef.current = false;
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [isMobile, splitRatio],
  );
  const liveStreamRef = useRef<HTMLVideoElement>(null);

  // Rename modal state
  const [renameModal, setRenameModal] = useState<{
    isOpen: boolean;
    session: ChatSession | null;
    newTitle: string;
  }>({
    isOpen: false,
    session: null,
    newTitle: '',
  });
  const [isRenaming, setIsRenaming] = useState(false);
  const [isVoiceProcessing, setIsVoiceProcessing] = useState(false);

  // Track intro video state - check session storage first
  const [hasPlayedIntro, setHasPlayedIntro] = useState(() => {
    // Only play if coming from home AND hasn't played yet in this session
    const fromHome = (location.state as { fromHome?: boolean })?.fromHome;
    if (!fromHome) return true; // Skip intro if not from home

    if (!companionId) return false;
    return sessionStorage.getItem(`hasPlayedIntro_${companionId}`) === 'true';
  });

  // Re-sync intro state only when companionId changes mid-session (not on first mount,
  // because useState already reads the correct value from sessionStorage above).
  // Skipping the first-mount call prevents VideoPlayer from re-firing its source-setting
  // effect with the same value, which caused an extra network request and black screen.
  const isFirstMountRef = useRef(true);
  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      return;
    }
    if (companionId) {
      const stored = sessionStorage.getItem(`hasPlayedIntro_${companionId}`) === 'true';
      setHasPlayedIntro(stored);
    }
  }, [companionId]);

  // Check if user has access to HeyGen avatar
  useEffect(() => {
    if (isLoggedIn) {
      checkHeyGenAccess()
        .then(({ hasAccess }) => setCanUseAvatar(hasAccess))
        .catch(() => setCanUseAvatar(false));
    }
  }, [isLoggedIn]);

  // Check authentication status
  useEffect(() => {
    // 1. Initial check
    const authenticated = isAuthenticated();
    setIsLoggedIn(authenticated);
    if (!authenticated) {
      signOut();
      navigate('/login', { state: { from: location.pathname + location.search }, replace: true });
      return;
    }

    // 2. Periodic check (covers manual token deletion in devtools and token expiration)
    const interval = setInterval(() => {
      if (!isAuthenticated()) {
        signOut();
        navigate('/', { replace: true });
      }
    }, 1000);

    // 3. Storage event listener (covers logouts/changes in other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'accessToken' || e.key === 'idToken' || e.key === 'tokenExpiresAt') {
        if (!isAuthenticated()) {
          signOut();
          navigate('/', { replace: true });
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [navigate, location.pathname, location.search]);

  // Get companion data
  const companion = useMemo(() => {
    if (!companionId) return null;
    const avatar = AVATARS[companionId];
    if (!avatar) return null;
    return getLocalizedAvatarInfo(avatar, i18n.language);
  }, [companionId, i18n.language]);

  // Load ALL user conversations on mount (like ChatGPT sidebar)
  useEffect(() => {
    const loadAllConversations = async () => {
      if (!isLoggedIn) return;

      try {
        const result = await getUserConversations();
        const sessions: ChatSession[] = result.conversations.map((conv) => ({
          id: conv.id,
          title: conv.title,
          date: formatDate(conv.lastMessageAt),
          dateGroup: getDateGroup(conv.lastMessageAt),
          avatarId: conv.avatarId,
          avatarName: conv.avatarName,
        }));
        setChatSessions(sessions);
      } catch (_err) {}
    };

    loadAllConversations();
  }, [isLoggedIn]);

  // Load specific conversation messages when session is selected
  useEffect(() => {
    const loadConversationMessages = async () => {
      if (!companionId || !isLoggedIn || !conversationId) return;

      // Skip reload for a conversation we just created — messages are already
      // in state from the optimistic add in handleSendMessage.
      if (skipHistoryForConversationRef.current === conversationId) {
        skipHistoryForConversationRef.current = null;
        return;
      }

      setIsLoadingHistory(true);
      try {
        const history = await getChatHistory(companionId, conversationId);

        if (history.history) {
          const getTimestampValue = (timestamp: string | undefined | null): number => {
            if (!timestamp) return 0;
            const parsed = new Date(timestamp).getTime();
            return Number.isNaN(parsed) ? 0 : parsed;
          };

          const sortedHistory = [...history.history].sort(
            (a, b) => getTimestampValue(a.timestamp) - getTimestampValue(b.timestamp),
          );

          const loadedMessages: Message[] = sortedHistory.map((msg) => ({
            id: msg.id,
            role: msg.sender === 'user' ? 'user' : 'assistant',
            content: msg.content,
            timestamp: formatTime(msg.timestamp),
            tokenUsage: msg.metadata?.tokenUsage
              ? {
                  totalTokens: msg.metadata.tokenUsage.totalTokens,
                  contextWindowSize: msg.metadata.tokenUsage.contextWindowSize,
                  historyTruncated: msg.metadata.tokenUsage.historyTruncated,
                }
              : undefined,
          }));
          setMessages(loadedMessages);
        }
      } catch (_err) {
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadConversationMessages();
  }, [companionId, conversationId, isLoggedIn]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length]);

  // Handle send message
  const handleSendMessage = useCallback(
    async (directMessage?: string) => {
      const messageContent = directMessage?.trim() || inputValue.trim();
      if (!messageContent || isLoading || !companionId) return;

      const userMessage: Message = {
        id: uniqueId('user'),
        role: 'user',
        content: messageContent,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };

      setMessages((prev) => appendMessagesIfNew(prev, userMessage));
      if (!directMessage) setInputValue('');
      setIsLoading(true);
      setError(null);

      try {
        // Call the real API
        const response = await sendChatMessage(
          companionId,
          messageContent,
          conversationId || undefined,
        );

        // Update conversation ID if this is a new conversation
        if (response.is_new_conversation && response.conversation_id) {
          // Mark this id so the history-load effect skips it — we already
          // have both messages in state from the optimistic adds below.
          skipHistoryForConversationRef.current = response.conversation_id;
          setConversationId(response.conversation_id);
          // Update URL
          const params = new URLSearchParams(window.location.search);
          params.set('session', response.conversation_id);
          navigate(`?${params.toString()}`, { replace: true });

          // Refresh sessions list to show the new one
          try {
            const updatedData = await getUserConversations();
            const sessions: ChatSession[] = updatedData.conversations.map((conv) => ({
              id: conv.id,
              title: conv.title,
              date: formatDate(conv.lastMessageAt),
              dateGroup: getDateGroup(conv.lastMessageAt),
              avatarId: conv.avatarId,
              avatarName: conv.avatarName,
            }));
            setChatSessions(sessions);
          } catch (_sessionsErr) {}
        }

        // Add assistant response to messages
        const assistantMessage: Message = {
          id: response.message_id || uniqueId('assistant'),
          role: 'assistant',
          content: response.avatar_response,
          timestamp: new Date(response.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }),
          tokenUsage: response.tokenUsage
            ? {
                totalTokens: response.tokenUsage.totalTokens,
                contextWindowSize: response.tokenUsage.contextWindowSize,
                historyTruncated: response.tokenUsage.historyTruncated,
              }
            : undefined,
        };
        setMessages((prev) => appendMessagesIfNew(prev, assistantMessage));

        // If avatar is live, make it speak the RAG response
        if (isLiveSession && heyGenAvatarRef.current) {
          try {
            await heyGenAvatarRef.current.speak(response.avatar_response);
          } catch (_speakErr) {}
        }
      } catch (error: unknown) {
        const err = error as Error;
        const errMsg = (err.message || '').toLowerCase();

        // Detect credit-related errors and show friendly modal instead of error banner
        if (errMsg.includes('daily') || errMsg.includes('limit') || errMsg.includes('quota')) {
          // Free user hit daily text limit
          setShowCreditModal('daily_limit');
          // Remove the user message for credit errors
          setMessages((prev) => prev.filter((m) => m.id !== userMessage.id));
          if (!directMessage) setInputValue(messageContent);
        } else if (errMsg.includes('insufficient') || errMsg.includes('credit')) {
          // Paid user out of credits
          setShowCreditModal('no_credits');
          // Remove the user message for credit errors
          setMessages((prev) => prev.filter((m) => m.id !== userMessage.id));
          if (!directMessage) setInputValue(messageContent);
        } else {
          // Network/timeout errors — keep the message in the thread with inline error + retry
          setFailedMessageId(userMessage.id);
          failedMessageContentRef.current = messageContent;
        }
      } finally {
        setIsLoading(false);
      }
    },
    [inputValue, isLoading, companionId, conversationId, isLiveSession, navigate],
  );

  // Retry a failed message
  const handleRetryMessage = useCallback(() => {
    const content = failedMessageContentRef.current;
    if (!content || !failedMessageId) return;

    // Remove the failed user message before retrying
    setMessages((prev) => prev.filter((m) => m.id !== failedMessageId));
    setFailedMessageId(null);
    failedMessageContentRef.current = null;

    // Re-send the message
    handleSendMessage(content);
  }, [failedMessageId, handleSendMessage]);

  // Dismiss inline error — remove the failed user message
  const handleDismissFailedMessage = useCallback(() => {
    if (failedMessageId) {
      setMessages((prev) => prev.filter((m) => m.id !== failedMessageId));
      setFailedMessageId(null);
      failedMessageContentRef.current = null;
    }
  }, [failedMessageId]);

  // Close extras menu on outside click
  useEffect(() => {
    if (!showExtrasMenu) return;
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      if (extrasMenuRef.current && !extrasMenuRef.current.contains(e.target as Element)) {
        setShowExtrasMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutside, true);
    document.addEventListener('touchstart', handleOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleOutside, true);
      document.removeEventListener('touchstart', handleOutside, true);
    };
  }, [showExtrasMenu]);

  // Auto-dismiss floating error banner after 6 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Web Speech API for voice input in chatbox
  const {
    isListening: isVoiceListening,
    isSupported: isVoiceSupported,
    toggleListening,
    stopListening,
    cancelListening,
  } = useWebSpeechSTT({
    onTranscript: (transcript: string) => {
      const trimmedTranscript = transcript.trim();
      if (!trimmedTranscript) {
        setIsVoiceProcessing(false);
        return;
      }
      // Populate input so the user can review before sending
      setInputValue((prev) => (prev ? `${prev} ${trimmedTranscript}` : trimmedTranscript));
      setIsVoiceProcessing(false);
    },
    onNoMatch: (_reason?: 'empty' | 'low_confidence') => {
      setIsVoiceProcessing(false);
    },
    onError: (errorMsg: string) => {
      setError(errorMsg);
      setIsVoiceProcessing(false);
    },
    language: sttLanguage,
  });

  // Handle voice button click
  const handleVoiceToggle = useCallback(() => {
    if (!isVoiceListening) {
      setIsVoiceProcessing(true);
      setError(null);
    }
    toggleListening();
  }, [isVoiceListening, toggleListening]);

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Copy message content to clipboard
  const handleCopyMessage = useCallback((content: string) => {
    navigator.clipboard.writeText(content);
  }, []);

  // Export entire chat history as a text file
  const handleExportChat = useCallback(() => {
    if (messages.length === 0) return;

    const chatTitle = companion?.displayName || t('chat.assistant', 'Assistant');
    const header = `${t('chat.exportTitle', 'Chat with')} ${chatTitle}\n${t(
      'chat.exportDate',
      'Exported on',
    )}: ${new Date().toLocaleString()}\n${'='.repeat(30)}\n\n`;

    const chatContent = messages
      .map((m) => {
        const role = m.role === 'user' ? t('chat.user', 'User') : chatTitle;
        return `[${m.timestamp}] ${role}: ${m.content}`;
      })
      .join('\n\n');

    const fullContent = header + chatContent;
    const blob = new Blob([fullContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `TheyChat_Export_${chatTitle.replace(/\s+/g, '_')}_${
      new Date().toISOString().split('T')[0]
    }.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [messages, companion, t]);

  // Handle new chat
  const handleNewChat = () => {
    setMessages([]);
    setInputValue('');
    setConversationId(null);
    setError(null);
    // Update URL to remove session param
    navigate(`/chat/${companionId}`, { replace: true });
  };

  // Handle session click - navigate to the correct companion's chat
  const handleSessionClick = (session: ChatSession) => {
    // Navigate to the companion that owns this conversation
    navigate(`/chat/${session.avatarId}?session=${session.id}`);
    setConversationId(session.id);
    // Messages will be loaded by the useEffect
  };

  // Handle delete conversation
  const handleDeleteConversation = async (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation(); // Prevent triggering session click

    if (
      !window.confirm(t('chat.deleteConfirm', 'Are you sure you want to delete this conversation?'))
    ) {
      return;
    }

    try {
      await deleteConversation(sessionId);
      // Remove from local state
      setChatSessions((prev) => prev.filter((s) => s.id !== sessionId));

      // If we deleted the current conversation, start fresh
      if (conversationId === sessionId) {
        handleNewChat();
      }
    } catch (_err) {
      setError(t('chat.deleteError', 'Failed to delete conversation'));
    }
  };

  // Open rename modal
  const openRenameModal = (e: React.MouseEvent, session: ChatSession) => {
    e.stopPropagation();
    setRenameModal({ isOpen: true, session, newTitle: session.title });
  };

  // Close rename modal
  const closeRenameModal = () => {
    setRenameModal({ isOpen: false, session: null, newTitle: '' });
  };

  // Handle rename conversation submit
  const handleRenameSubmit = async () => {
    if (
      !renameModal.session ||
      !renameModal.newTitle.trim() ||
      renameModal.newTitle === renameModal.session.title
    ) {
      closeRenameModal();
      return;
    }

    setIsRenaming(true);
    try {
      await updateConversation(renameModal.session.id, { title: renameModal.newTitle.trim() });
      // Update local state
      setChatSessions((prev) =>
        prev.map((s) =>
          s.id === renameModal.session?.id ? { ...s, title: renameModal.newTitle.trim() } : s,
        ),
      );
      closeRenameModal();
    } catch (_err) {
      setError(t('chat.renameError', 'Failed to rename conversation'));
    } finally {
      setIsRenaming(false);
    }
  };

  // Handle rename input key press
  const handleRenameKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleRenameSubmit();
    } else if (e.key === 'Escape') {
      closeRenameModal();
    }
  };

  // Filter sessions by search
  const filteredSessions = chatSessions.filter(
    (session) =>
      session.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      session.avatarName?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Group sessions by date
  const todaySessions = filteredSessions.filter((s) => s.dateGroup === 'today');
  const weekSessions = filteredSessions.filter((s) => s.dateGroup === 'week');
  const olderSessions = filteredSessions.filter((s) => s.dateGroup === 'older');

  if (!initialAuthCheck) {
    signOut();
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }

  if (!companion) {
    return (
      <div className="chat-page">
        <Header isLoggedIn={isLoggedIn} />
        <main className="chat-main">
          <div className="chat-not-found">
            <h2>{t('chat.companionNotFound', 'Companion not found')}</h2>
            <Link to="/">{t('chat.backToHome', 'Back to Home')}</Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={`chat-page ${isLiveSession ? 'live-session-active' : ''}`}>
      {/* Background effects */}
      <div className="chat-bg-accent" />
      <div className="chat-bg-sand-1" />
      <div className="chat-bg-sand-2" />

      {!isLiveSession && <Header isLoggedIn={isLoggedIn} />}

      <main className="chat-main">
        {/* Error Display */}
        {error && (
          <div className="chat-error">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <title>Error Icon</title>
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)} aria-label="Dismiss error">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <title>Dismiss Icon</title>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        )}

        {/* Credits / Daily Limit Modal */}
        {showCreditModal && (
          <button
            type="button"
            className="credit-modal-overlay"
            onClick={() => setShowCreditModal(false)}
            aria-label="Close modal"
          >
            <div
              className="credit-modal"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="credit-modal-title"
            >
              <div className="credit-modal-icon">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9a7b55"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <title>Credit Info Icon</title>
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
              </div>
              {showCreditModal === 'daily_limit' ? (
                <>
                  <h3 id="credit-modal-title" className="credit-modal-title">
                    {t('chat.dailyLimitTitle', 'Daily Limit Reached')}
                  </h3>
                  <p className="credit-modal-desc">
                    {t(
                      'chat.dailyLimitDesc',
                      "You've reached your free daily message limit. Subscribe to get more messages and unlock premium features.",
                    )}
                  </p>
                  <div className="credit-modal-actions">
                    <Link
                      to="/subscription"
                      className="credit-modal-btn-primary"
                      onClick={() => setShowCreditModal(false)}
                    >
                      {t('chat.subscribNow', 'Subscribe Now')}
                    </Link>
                  </div>
                </>
              ) : addonPurchaseState.success ? (
                <>
                  <h3 id="credit-modal-title" className="credit-modal-title">
                    {t('chat.creditsAdded', 'Credits Added!')}
                  </h3>
                  <p className="credit-modal-desc">
                    {t(
                      'chat.creditsAddedDesc',
                      'Your credits have been added. You can now continue your session.',
                    )}
                  </p>
                  <div className="credit-modal-actions">
                    <button
                      type="button"
                      className="credit-modal-btn-primary"
                      onClick={() => {
                        setShowCreditModal(false);
                        setAddonPurchaseState({ loading: null, success: false, error: null });
                      }}
                    >
                      {t('chat.continueChat', 'Continue')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3 id="credit-modal-title" className="credit-modal-title">
                    {t('chat.noCreditsTitle', 'Out of Credits')}
                  </h3>
                  <p className="credit-modal-desc">
                    {t(
                      'chat.noCreditsDesc',
                      'Purchase addon credits to continue your session instantly.',
                    )}
                  </p>

                  {addonPurchaseState.error && (
                    <p className="credit-modal-error">
                      {addonPurchaseState.error === 'NO_PAYMENT_METHOD'
                        ? t(
                            'chat.noPaymentMethod',
                            'Please subscribe first to save your payment details.',
                          )
                        : addonPurchaseState.error}
                    </p>
                  )}

                  <div className="credit-pack-grid">
                    {[
                      { id: 'pack_10', credits: 10, price: '$1.99' },
                      { id: 'pack_50', credits: 50, price: '$5.99' },
                      { id: 'pack_100', credits: 100, price: '$9.99' },
                    ].map((pack) => (
                      <button
                        type="button"
                        key={pack.id}
                        className={`credit-pack-btn ${
                          addonPurchaseState.loading === pack.id ? 'loading' : ''
                        }`}
                        disabled={addonPurchaseState.loading !== null}
                        onClick={async () => {
                          let token: string | null = null;
                          try {
                            setAddonPurchaseState({
                              loading: pack.id,
                              success: false,
                              error: null,
                            });
                            token = await getIdToken();
                            if (!token) {
                              setAddonPurchaseState({
                                loading: null,
                                success: false,
                                error: 'Not authenticated',
                              });
                              return;
                            }
                            await purchaseAddonDirect(pack.id, token);
                            setAddonPurchaseState({ loading: null, success: true, error: null });
                          } catch (err) {
                            const errMsg = err instanceof Error ? err.message : 'Purchase failed';

                            // Fallback to Stripe Checkout on NO_PAYMENT_METHOD or CARD_DECLINED
                            if (
                              errMsg.includes('NO_PAYMENT_METHOD') ||
                              errMsg.includes('CARD_DECLINED')
                            ) {
                              try {
                                setAddonPurchaseState({
                                  loading: pack.id,
                                  success: false,
                                  error: 'Redirecting to secure checkout...',
                                });
                                await checkoutStripeCredits(pack.id, token as string);
                                return; // Browser will redirect
                              } catch (fallbackErr) {
                                const fallbackErrMsg =
                                  fallbackErr instanceof Error
                                    ? fallbackErr.message
                                    : 'Checkout redirect failed';
                                setAddonPurchaseState({
                                  loading: null,
                                  success: false,
                                  error: fallbackErrMsg,
                                });
                              }
                            } else {
                              setAddonPurchaseState({
                                loading: null,
                                success: false,
                                error: errMsg,
                              });
                            }
                          }
                        }}
                      >
                        <span className="credit-pack-amount">{pack.credits}</span>
                        <span className="credit-pack-label">{t('chat.credits', 'Credits')}</span>
                        <span className="credit-pack-price">{pack.price}</span>
                        {addonPurchaseState.loading === pack.id && (
                          <span className="credit-pack-spinner" />
                        )}
                      </button>
                    ))}
                  </div>

                  <div className="credit-modal-actions">
                    <Link
                      to="/subscription"
                      className="credit-modal-btn-secondary"
                      onClick={() => setShowCreditModal(false)}
                    >
                      {t('chat.viewPlans', 'View Plans')}
                    </Link>
                    <button
                      type="button"
                      className="credit-modal-btn-dismiss"
                      onClick={() => {
                        setShowCreditModal(false);
                        setAddonPurchaseState({ loading: null, success: false, error: null });
                      }}
                    >
                      {t('chat.dismiss', 'Dismiss')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </button>
        )}

        {/* Sidebar Overlay - mobile only */}
        {isMobile && showSidebar && (
          <button
            type="button"
            className="sidebar-overlay"
            onClick={() => setShowSidebar(false)}
            aria-label="Close sidebar"
          />
        )}

        {/* Fixed Hamburger Menu Button (always visible, always same position) */}
        <button
          type="button"
          className="sidebar-toggle-fixed"
          onClick={() => setShowSidebar(!showSidebar)}
          title={
            showSidebar
              ? t('chat.closeChatHistory', 'Close Chat History')
              : t('chat.openChatHistory', 'Open Chat History')
          }
          aria-label={
            showSidebar
              ? t('chat.closeChatHistory', 'Close Chat History')
              : t('chat.openChatHistory', 'Open Chat History')
          }
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <title>Toggle Sidebar Icon</title>
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <line x1="9" y1="3" x2="9" y2="21" />
          </svg>
        </button>

        {/* Sidebar */}
        <aside
          className={`chat-sidebar ${showSidebar ? 'open' : 'closed'} ${isLiveSession ? 'live-mode' : ''}`}
        >
          {/* Companion Info */}
          <div className="sidebar-companion-info">
            <div className="sidebar-companion-header">
              <span className="sidebar-companion-title">
                {t('chat.chatTo', 'Chat to')} {companion.displayName}
              </span>
              <button
                type="button"
                className="info-btn"
                aria-label={t('chat.viewProfile', 'View profile')}
                onClick={() => navigate(`/companion/${companionId}`)}
                data-tooltip={t('chat.viewProfile', 'View profile')}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="7" r="1" fill="currentColor" stroke="none" />
                  <path d="M10.5 11h1.5v5.5M10 16.5h4" strokeLinecap="round" />
                  <circle cx="12" cy="7" r="1" fill="currentColor" stroke="none" />
                  <path d="M10.5 11h1.5v5.5M10 16.5h4" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>

          {/* Recent Chats */}
          <div className="sidebar-recent">
            <div className="sidebar-recent-header">
              <span>{t('chat.recentChats', 'Recent chats')}</span>
              <button
                type="button"
                className="add-chat-btn"
                onClick={handleNewChat}
                aria-label={t('chat.newChatWithSahabe', 'New chat with {{name}}', {
                  name: companion.displayName,
                })}
                data-tooltip={t('chat.newChatWithSahabe', 'New chat with {{name}}', {
                  name: companion.displayName,
                })}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
            </div>

            {/* Search */}
            <div className="sidebar-search">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#4D4D4D"
                strokeWidth="1.5"
                aria-hidden="true"
              >
                <title>Search Icon</title>
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder={t('chat.searchPlaceholder', 'Search for chat...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Chat Sessions List */}
            <div className="sidebar-sessions">
              {todaySessions.length > 0 && (
                <div className="session-group">
                  {todaySessions.map((session) => (
                    <button
                      type="button"
                      key={session.id}
                      className={`session-item ${conversationId === session.id ? 'active' : ''} ${session.avatarId === companionId ? 'current-companion' : ''}`}
                      onClick={() => handleSessionClick(session)}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <title>Chat Session Icon</title>
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                      <div className="session-item-content">
                        <span className="session-item-title">{session.title}</span>
                        {session.avatarName && session.avatarId !== companionId && (
                          <span className="session-item-avatar">{session.avatarName}</span>
                        )}
                      </div>
                      <div className="session-actions">
                        <button
                          type="button"
                          className="session-action-btn"
                          onClick={(e) => openRenameModal(e, session)}
                          title={t('chat.rename', 'Rename')}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            aria-hidden="true"
                          >
                            <title>Rename Icon</title>
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="session-action-btn delete"
                          onClick={(e) => handleDeleteConversation(e, session.id)}
                          title={t('chat.delete', 'Delete')}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            aria-hidden="true"
                          >
                            <title>Delete Icon</title>
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {weekSessions.length > 0 && (
                <div className="session-group">
                  <span className="session-group-label">
                    {t('chat.sevenDaysAgo', '7 days ago')}
                  </span>
                  {weekSessions.map((session) => (
                    <button
                      type="button"
                      key={session.id}
                      className={`session-item ${conversationId === session.id ? 'active' : ''} ${session.avatarId === companionId ? 'current-companion' : ''}`}
                      onClick={() => handleSessionClick(session)}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <title>Chat Session Icon</title>
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                      <div className="session-item-content">
                        <span className="session-item-title">{session.title}</span>
                        {session.avatarName && session.avatarId !== companionId && (
                          <span className="session-item-avatar">{session.avatarName}</span>
                        )}
                      </div>
                      <div className="session-actions">
                        <button
                          type="button"
                          className="session-action-btn"
                          onClick={(e) => openRenameModal(e, session)}
                          title={t('chat.rename', 'Rename')}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            aria-hidden="true"
                          >
                            <title>Rename Icon</title>
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="session-action-btn delete"
                          onClick={(e) => handleDeleteConversation(e, session.id)}
                          title={t('chat.delete', 'Delete')}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            aria-hidden="true"
                          >
                            <title>Delete Icon</title>
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {olderSessions.length > 0 && (
                <div className="session-group">
                  <span className="session-group-label">{t('chat.older', 'Older')}</span>
                  {olderSessions.map((session) => (
                    <button
                      type="button"
                      key={session.id}
                      className={`session-item ${conversationId === session.id ? 'active' : ''} ${session.avatarId === companionId ? 'current-companion' : ''}`}
                      onClick={() => handleSessionClick(session)}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <title>Chat Session Icon</title>
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                      <div className="session-item-content">
                        <span className="session-item-title">{session.title}</span>
                        {session.avatarName && session.avatarId !== companionId && (
                          <span className="session-item-avatar">{session.avatarName}</span>
                        )}
                      </div>
                      <div className="session-actions">
                        <button
                          type="button"
                          className="session-action-btn"
                          onClick={(e) => openRenameModal(e, session)}
                          title={t('chat.rename', 'Rename')}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            aria-hidden="true"
                          >
                            <title>Rename Icon</title>
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="session-action-btn delete"
                          onClick={(e) => handleDeleteConversation(e, session.id)}
                          title={t('chat.delete', 'Delete')}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            aria-hidden="true"
                          >
                            <title>Delete Icon</title>
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </aside>
        {/* Resizable split view wrapper */}
        <div className="chat-split-wrapper">
          <div
            ref={splitContainerRef}
            className={`chat-content-split ${isLiveSession && !showDesktopChat ? 'full-width' : ''}`}
          >
            {/* Avatar panel — flex width controlled by splitRatio on desktop */}
            <div
              className={`avatar-video-section${avatarChromeHidden && isLiveSession && !isOverlayFocused ? ' chrome-hidden' : ''}`}
              style={
                !isMobile && !(isLiveSession && !showDesktopChat)
                  ? { flex: `0 0 ${splitRatio}%` }
                  : undefined
              }
            >
              {isLiveSession && canUseAvatar ? (
                <div className="heygen-avatar-wrapper">
                  <HeyGenAvatar
                    ref={heyGenAvatarRef}
                    companionId={companionId || ''}
                    companionImage={companion.image}
                    companionName={companion.displayName}
                    conversationId={conversationId}
                    sttLanguage={sttLanguage}
                    showMobileChatOverlay={showMobileChatOverlay}
                    showFollowUpQuestions={showFollowUpQuestions}
                    onToggleMobileChatOverlay={() =>
                      setShowMobileChatOverlay(!showMobileChatOverlay)
                    }
                    // Desktop Chat Control
                    showDesktopChat={showDesktopChat}
                    onToggleDesktopChat={() => setShowDesktopChat(!showDesktopChat)}
                    onClose={() => {
                      setIsLiveSession(false);
                      setIsAvatarConnecting(false);
                      setShowSessionEnded(true);
                    }}
                    onCancelStart={() => {
                      setIsLiveSession(false);
                      setIsAvatarConnecting(false);
                      setShowSessionEnded(false);
                    }}
                    onConnecting={() => setIsAvatarConnecting(true)}
                    onConnected={() => setIsAvatarConnecting(false)}
                    onError={(err) => {
                      setIsAvatarConnecting(false);
                      const msg = (err?.message || '').toLowerCase();
                      if (
                        msg.includes('insufficient') ||
                        msg.includes('credit') ||
                        msg.includes('denied') ||
                        msg.includes('403')
                      ) {
                        setShowCreditModal('no_credits');
                      }
                    }}
                    onCreditError={(type) => setShowCreditModal(type)}
                    onBuyCredits={() => setShowCreditModal('no_credits')}
                    onConversationCreated={(newConversationId) => {
                      setConversationId(newConversationId);
                      searchParams.set('session', newConversationId);
                      navigate(`?${searchParams.toString()}`, { replace: true });
                    }}
                    onMessageSent={(userMessage, avatarResponse) => {
                      // Sync messages to main chat display
                      const timestamp = new Date().toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      });
                      setMessages((prev) =>
                        appendMessagesIfNew(
                          prev,
                          {
                            id: uniqueId('user'),
                            role: 'user' as const,
                            content: userMessage,
                            timestamp,
                          },
                          {
                            id: uniqueId('assistant'),
                            role: 'assistant' as const,
                            content: avatarResponse,
                            timestamp,
                          },
                        ),
                      );
                      // Show mobile chat overlay when messages arrive
                      if (isMobile) setShowMobileChatOverlay(true);
                    }}
                    onChromeVisibilityChange={(visible) => setAvatarChromeHidden(!visible)}
                  />
                </div>
              ) : (
                <VideoPlayer
                  companionId={companionId || ''}
                  companionImage={companion.image}
                  companionName={companion.displayName}
                  isLiveSession={isLiveSession}
                  onStartLiveAvatar={() => {
                    if (canUseAvatar) {
                      setIsLiveSession(true);
                      setIsAvatarConnecting(true); // Hide chat overlay during loading screen
                      setHasPlayedIntro(true);
                      sessionStorage.setItem(`hasPlayedIntro_${companionId}`, 'true');
                    }
                  }}
                  onStopLiveAvatar={() => {
                    setIsLiveSession(false);
                    setShowSessionEnded(true);
                  }}
                  liveStreamRef={liveStreamRef}
                  hasPlayedIntro={hasPlayedIntro}
                  onIntroEnd={() => {
                    setHasPlayedIntro(true);
                    sessionStorage.setItem(`hasPlayedIntro_${companionId}`, 'true');
                  }}
                  hasMessages={messages.length > 0}
                  isMobile={isMobile}
                  isChatVisible={showMobileChatOverlay}
                />
              )}

              {/* Session Ended Overlay */}
              {showSessionEnded && !isLiveSession && (
                <SessionEndedOverlay
                  companionImage={companion.image}
                  companionName={companion.displayName}
                  avatarId={companionId || ''}
                  conversationId={conversationId}
                  onStartNewSession={() => {
                    setShowSessionEnded(false);
                    setIsLiveSession(true);
                    setHasPlayedIntro(true);
                    sessionStorage.setItem(`hasPlayedIntro_${companionId}`, 'true');
                  }}
                  onClose={() => setShowSessionEnded(false)}
                />
              )}

              {/* Mobile Chat Overlay visibility rules:
                  ✅ Idle (VideoPlayer mode) — always shown, messages panel opens on first send
                  ❌ isAvatarConnecting — hidden during golden moon loading screen
                  ✅ Live session active — shown fully
                  ❌ showSessionEnded — hidden on the Session Ended / feedback screen */}
              {isMobile &&
                companion &&
                !showSidebar &&
                !isAvatarConnecting &&
                !showSessionEnded && (
                  <LiveChatOverlay
                    messages={messages}
                    isVisible={showMobileChatOverlay}
                    onToggle={() => setShowMobileChatOverlay(!showMobileChatOverlay)}
                    companionImage={companion.image}
                    companionName={companion.displayName}
                    onSendMessage={handleSendMessage}
                    isVoiceSupported={isVoiceSupported}
                    isVoiceListening={isVoiceListening}
                    onToggleVoice={handleVoiceToggle}
                    onStopVoice={stopListening}
                    onCancelVoice={() => {
                      cancelListening();
                      setIsVoiceProcessing(false);
                    }}
                    isProcessing={isVoiceProcessing && !isVoiceListening}
                    inputValue={inputValue}
                    onInputChange={setInputValue}
                    isLoading={isLoading}
                    isLiveSession={isLiveSession}
                    sttLanguage={sttLanguage}
                    onSttLanguageChange={setSttLanguage}
                    onStartLiveSession={() => {
                      if (canUseAvatar) {
                        setIsLiveSession(true);
                        setIsAvatarConnecting(true); // Hide input immediately when LIVE is tapped
                        setHasPlayedIntro(true);
                        sessionStorage.setItem(`hasPlayedIntro_${companionId}`, 'true');
                      }
                    }}
                    onFocusChange={setIsOverlayFocused}
                    showFollowUpQuestions={showFollowUpQuestions}
                    onToggleFollowUp={toggleFollowUpQuestions}
                  />
                )}
            </div>

            {/* Drag handle between avatar and chat panels — desktop only */}
            {!isMobile && (!isLiveSession || showDesktopChat) && (
              <div
                className="panel-resize-handle"
                onMouseDown={handleResizeMouseDown}
                title="Drag to resize panels"
                aria-hidden="true"
              />
            )}

            {/* Chat panel */}
            {(!isLiveSession || showDesktopChat) && (
              <div className="chat-section">
                <div className="chat-container">
                  {/* Chat Header / Welcome */}
                  {messages.length === 0 ? (
                    <div className="chat-content-scrollable">
                      <div className="chat-welcome">
                        <div className="chat-welcome-avatar">
                          <img src={companion.image} alt={companion.displayName} />
                        </div>
                        <div className="chat-welcome-text">
                          <div className="chat-welcome-label">
                            <div className="welcome-line" />
                            <span>
                              {t('chat.startConversationWith', 'Start conversation with')}
                            </span>
                            <div className="welcome-line" />
                          </div>
                          <h1 className="chat-welcome-name">{companion.displayName}</h1>
                        </div>
                      </div>

                      <div className="chat-prompts">
                        <button
                          type="button"
                          className="prompt-card"
                          onClick={() =>
                            setInputValue(
                              t(
                                'chat.prompt1',
                                'Tell me about your most memorable experience with the Prophet (PBUH).',
                              ),
                            )
                          }
                        >
                          <div className="prompt-icon source">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              aria-hidden="true"
                            >
                              <title>Source Icon</title>
                              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                            </svg>
                          </div>
                          <span>
                            {t(
                              'chat.prompt1',
                              'Tell me about your most memorable experience with the Prophet (PBUH).',
                            )}
                          </span>
                        </button>
                        <button
                          type="button"
                          className="prompt-card"
                          onClick={() =>
                            setInputValue(
                              t('chat.prompt2', 'What lessons can we learn from your life?'),
                            )
                          }
                        >
                          <div className="prompt-icon question">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              aria-hidden="true"
                            >
                              <title>Question Icon</title>
                              <circle cx="12" cy="12" r="10" />
                              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                              <line x1="12" y1="17" x2="12.01" y2="17" />
                            </svg>
                          </div>
                          <span>
                            {t('chat.prompt2', 'What lessons can we learn from your life?')}
                          </span>
                        </button>
                        <button
                          type="button"
                          className="prompt-card"
                          onClick={() =>
                            setInputValue(t('chat.prompt3', 'How did you first embrace Islam?'))
                          }
                        >
                          <div className="prompt-icon ai">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              aria-hidden="true"
                            >
                              <title>AI Icon</title>
                              <path d="M12 2a10 10 0 0 1 10 10 10 10 0 0 1-10 10A10 10 0 0 1 2 12 10 10 0 0 1 12 2z" />
                              <path d="M12 6v6l4 2" />
                            </svg>
                          </div>
                          <span>{t('chat.prompt3', 'How did you first embrace Islam?')}</span>
                        </button>
                        <button
                          type="button"
                          className="prompt-card"
                          onClick={() =>
                            setInputValue(
                              t('chat.prompt4', 'What advice would you give to Muslims today?'),
                            )
                          }
                        >
                          <div className="prompt-icon voice">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              aria-hidden="true"
                            >
                              <title>Voice Icon</title>
                              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                              <line x1="12" y1="19" x2="12" y2="23" />
                              <line x1="8" y1="23" x2="16" y2="23" />
                            </svg>
                          </div>
                          <span>
                            {t('chat.prompt4', 'What advice would you give to Muslims today?')}
                          </span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="chat-messages">
                      {messages.map((message) => (
                        <div
                          key={message.id}
                          className={`chat-message ${message.role}${failedMessageId === message.id ? ' failed' : ''}`}
                        >
                          {message.role === 'assistant' && (
                            <div className="message-avatar">
                              <img src={companion.image} alt={companion.displayName} />
                            </div>
                          )}
                          <div className="message-content-wrapper">
                            <div className="message-bubble">
                              <MessageContent
                                content={message.content}
                                isAssistant={message.role === 'assistant'}
                                companionName={
                                  message.role === 'assistant' ? companion.displayName : undefined
                                }
                                onQuestionClick={(q) => handleSendMessage(q)}
                                disabled={isLoading}
                                showFollowUp={showFollowUpQuestions}
                              />
                            </div>
                            <div className="message-meta">
                              <span className="message-time">{message.timestamp}</span>
                              {message.role === 'assistant' && (
                                <div className="message-actions">
                                  <button
                                    type="button"
                                    className="action-btn"
                                    title={t('chat.copy', 'Copy')}
                                    onClick={() => handleCopyMessage(message.content)}
                                  >
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      aria-hidden="true"
                                    >
                                      <title>Copy</title>
                                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                    </svg>
                                  </button>
                                  <button
                                    type="button"
                                    className="action-btn"
                                    title={t('chat.export', 'Export Chat')}
                                    onClick={handleExportChat}
                                  >
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      aria-hidden="true"
                                    >
                                      <title>Export</title>
                                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                      <polyline points="7 10 12 15 17 10" />
                                      <line x1="12" y1="15" x2="12" y2="3" />
                                    </svg>
                                  </button>
                                  {message.tokenUsage && (
                                    <button
                                      type="button"
                                      className="action-btn"
                                      style={{ cursor: 'pointer' }}
                                      title={
                                        message.tokenUsage.historyTruncated
                                          ? t('chat.tokenUsageTooltipFull', {
                                              total: message.tokenUsage.totalTokens,
                                              size: message.tokenUsage.contextWindowSize,
                                            })
                                          : t('chat.tokenUsageTooltip', {
                                              total: message.tokenUsage.totalTokens,
                                              size: message.tokenUsage.contextWindowSize,
                                            })
                                      }
                                      onClick={(e) => {
                                        // On mobile, this helps trigger the title tooltip reliably without an alert
                                        e.preventDefault();
                                      }}
                                    >
                                      <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke={
                                          message.tokenUsage.historyTruncated
                                            ? '#eab308'
                                            : 'currentColor'
                                        }
                                        strokeWidth="1.5"
                                        aria-hidden="true"
                                      >
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="12" y1="16" x2="12" y2="12"></line>
                                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                      </svg>
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          {message.role === 'user' && (
                            <div className="message-avatar user-avatar">
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                aria-hidden="true"
                              >
                                <title>User Avatar</title>
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                <circle cx="12" cy="7" r="4" />
                              </svg>
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Inline error for failed messages */}
                      {failedMessageId && (
                        <div className="chat-inline-error">
                          <div className="chat-inline-error-content">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              aria-hidden="true"
                            >
                              <title>Error</title>
                              <circle cx="12" cy="12" r="10" />
                              <line x1="12" y1="8" x2="12" y2="12" />
                              <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            <span>
                              {t(
                                'chat.messageFailed',
                                'Message could not be delivered. Please check your connection.',
                              )}
                            </span>
                          </div>
                          <div className="chat-inline-error-actions">
                            <button
                              type="button"
                              className="chat-inline-retry-btn"
                              onClick={handleRetryMessage}
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                aria-hidden="true"
                              >
                                <title>Retry</title>
                                <polyline points="23 4 23 10 17 10" />
                                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                              </svg>
                              {t('chat.retry', 'Retry')}
                            </button>
                            <button
                              type="button"
                              className="chat-inline-dismiss-btn"
                              onClick={handleDismissFailedMessage}
                            >
                              {t('chat.dismiss', 'Dismiss')}
                            </button>
                          </div>
                        </div>
                      )}

                      {isLoading && (
                        <div className="chat-message assistant loading">
                          <div className="message-avatar">
                            <img src={companion.image} alt={companion.displayName} />
                          </div>
                          <div className="message-bubble">
                            <div className="typing-indicator">
                              <span />
                              <span />
                              <span />
                            </div>
                          </div>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  )}

                  {/* Suggested Prompts (when no messages) moved into chat-content-scrollable */}

                  {/* Input Area */}
                  <div className="chat-input-container">
                    <div
                      className={`chat-input-wrapper${isVoiceListening || isVoiceProcessing ? ' recording' : ''}`}
                    >
                      {isVoiceListening || (isVoiceProcessing && !isVoiceListening) ? (
                        <VoiceRecordingBar
                          variant="chatbox"
                          isProcessing={isVoiceProcessing && !isVoiceListening}
                          onCancel={() => {
                            cancelListening();
                            setIsVoiceProcessing(false);
                          }}
                          onConfirm={() => {
                            stopListening();
                          }}
                        />
                      ) : (
                        <>
                          <div className="chat-input-top-row">
                            {/* Extras menu — Gemini-style + button */}
                            <div className="chat-extras-wrapper" ref={extrasMenuRef}>
                              {/* Popover panel — renders above the button */}
                              {showExtrasMenu && (
                                <div className="chat-extras-menu">
                                  {/* Row: Suggested Follow-Up Questions */}
                                  <div className="chat-extras-menu-item">
                                    <div className="chat-extras-menu-item-icon">
                                      <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        aria-hidden="true"
                                      >
                                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                      </svg>
                                    </div>
                                    <span className="chat-extras-menu-item-label">
                                      {t('chat.suggestedQuestions', 'Suggested Questions')}
                                    </span>
                                    <button
                                      type="button"
                                      className={`chat-extras-toggle${showFollowUpQuestions ? ' on' : ''}`}
                                      onClick={toggleFollowUpQuestions}
                                      aria-label={
                                        showFollowUpQuestions
                                          ? t(
                                              'chat.suggestedQuestionsOn',
                                              'Suggested questions on — click to disable',
                                            )
                                          : t(
                                              'chat.suggestedQuestionsOff',
                                              'Suggested questions off — click to enable',
                                            )
                                      }
                                      role="switch"
                                      aria-checked={showFollowUpQuestions}
                                    >
                                      <span className="chat-extras-toggle-thumb" />
                                    </button>
                                  </div>
                                  {/* Future settings rows go here */}
                                </div>
                              )}
                              {/* The + / ✕ trigger button */}
                              <button
                                type="button"
                                className={`chat-extras-btn${showExtrasMenu ? ' open' : ''}`}
                                onClick={toggleExtrasMenu}
                                aria-label={
                                  showExtrasMenu
                                    ? t('chat.closeExtras', 'Close extras')
                                    : t('chat.openExtras', 'More options')
                                }
                                aria-expanded={showExtrasMenu}
                              >
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <line x1="12" y1="5" x2="12" y2="19" />
                                  <line x1="5" y1="12" x2="19" y2="12" />
                                </svg>
                              </button>
                            </div>
                            <textarea
                              ref={inputRef}
                              value={inputValue}
                              onChange={(e) => setInputValue(e.target.value)}
                              onKeyPress={handleKeyPress}
                              placeholder={t('chat.inputPlaceholder', 'Type your text ...')}
                              rows={1}
                              disabled={isLoading}
                            />
                            {inputValue.trim() && (
                              <button
                                type="button"
                                className="send-btn"
                                onClick={() => handleSendMessage()}
                                disabled={isLoading}
                                aria-label="Send message"
                                title={t('chat.sendMessage', 'Send message')}
                              >
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <title>{t('chat.sendMessage', 'Send message')}</title>
                                  <line x1="22" y1="2" x2="11" y2="13" />
                                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                                </svg>
                              </button>
                            )}
                          </div>

                          <div className="chat-input-bottom-row">
                            <LanguageSelector
                              value={sttLanguage}
                              onChange={setSttLanguage}
                              variant="mobile"
                              className="chatbox-lang-selector"
                              disabled={isLiveSession}
                            />
                            <div className="chat-input-actions">
                              <button
                                type="button"
                                className="chat-voice-btn"
                                onClick={handleVoiceToggle}
                                disabled={!isVoiceSupported || isLoading}
                                aria-label={t('chat.startListening', 'Tap to speak')}
                                title={
                                  !isVoiceSupported
                                    ? t(
                                        'chat.voiceNotSupported',
                                        'Voice not supported in this browser',
                                      )
                                    : t('chat.startListening', 'Tap to speak')
                                }
                              >
                                <svg
                                  className="voice-btn-icon"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  aria-hidden="true"
                                >
                                  <title>Microphone Icon</title>
                                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                                  <line x1="12" y1="19" x2="12" y2="23" />
                                  <line x1="8" y1="23" x2="16" y2="23" />
                                </svg>
                              </button>

                              {!isLiveSession && (
                                <button
                                  type="button"
                                  className="chat-start-live-btn"
                                  onClick={() => {
                                    if (canUseAvatar) {
                                      setIsLiveSession(true);
                                      setIsAvatarConnecting(true);
                                      setHasPlayedIntro(true);
                                      sessionStorage.setItem(
                                        `hasPlayedIntro_${companionId}`,
                                        'true',
                                      );
                                    }
                                  }}
                                  aria-label={t('chat.startLiveSession', 'Start Live Session')}
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    aria-hidden="true"
                                  >
                                    <polygon points="5 3 19 12 5 21 5 3" />
                                  </svg>
                                  <span>{t('chat.live', 'LIVE')}</span>
                                </button>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Modern Premium Footer */}
      {!isMobile && !isOverlayFocused && (
        <footer className="chat-footer">
          <div className="chat-footer-content">
            <div className="chat-footer-links">
              <Link to="/privacy">{t('footer.privacyPolicy', 'Privacy Policy')}</Link>
              <Link to="/terms">{t('footer.terms', 'Terms of Service')}</Link>
              <Link to="/contact">{t('header.contactUs', 'Contact Us')}</Link>
            </div>
            <div className="chat-footer-copyright">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                aria-hidden="true"
              >
                <title>Copyright Icon</title>
                <circle cx="12" cy="12" r="10" />
                <path d="M14.83 14.83a4 4 0 1 1 0-5.66" />
              </svg>
              <span>
                {t('footer.copyright', 'CopyRight')} {new Date().getFullYear()},{' '}
                {t('footer.allRightsReserved', 'All rights reserved')}
              </span>
            </div>
          </div>
        </footer>
      )}

      {renameModal.isOpen && (
        <button
          type="button"
          className="rename-modal-overlay"
          onClick={closeRenameModal}
          aria-label="Close modal"
        >
          <div
            className="rename-modal"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="rename-modal-title"
          >
            <div className="rename-modal-header">
              <h3 id="rename-modal-title">{t('chat.renameConversation', 'Rename Conversation')}</h3>
              <button type="button" className="rename-modal-close" onClick={closeRenameModal}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <title>Close Icon</title>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="rename-modal-body">
              <label htmlFor="rename-input">{t('chat.newName', 'New name')}</label>
              <input
                id="rename-input"
                type="text"
                value={renameModal.newTitle}
                onChange={(e) => setRenameModal((prev) => ({ ...prev, newTitle: e.target.value }))}
                onKeyDown={handleRenameKeyPress}
                placeholder={t('chat.enterNewName', 'Enter new name...')}
              />
            </div>
            <div className="rename-modal-footer">
              <button type="button" className="rename-modal-cancel" onClick={closeRenameModal}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                className="rename-modal-save"
                onClick={handleRenameSubmit}
                disabled={isRenaming || !renameModal.newTitle.trim()}
              >
                {isRenaming ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
              </button>
            </div>
          </div>
        </button>
      )}
    </div>
  );
};

export default Chat;
